import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import classNames from 'classnames';
import { EventInput } from 'fullcalendar';
import { useState } from 'react';
import { Loading } from 'react-daisyui';
import Avatars from '../../../../../components/Avatars/Avatars.component';
import { randomString } from '../../../../../functions/string.functions';
import { useWorkspaceContext } from '../../../../../providers/WorkspaceProvider/Workspace.context';
import { GetInitialsName } from '../../../../../utils/GetInitialsName';
import { eAgendaTabs, eCalendarView } from '../Calendar.enums';
import { useCalendarContext } from '../Calendar.hook';
import { tCalendarView } from '../Calendar.types';
import { HeaderDatePicker } from './Datepicker.component';
import './HeaderToolbar.styles.scss';

export default function HeaderToolbar(): React.ReactElement {
  const {
    currentDateTitle,
    currentDate,
    onNavigate,
    onChangeDatepicker,
    changeView,
    currentView,
    currentTab,
    events,
    setEvents,
    loading,
  } = useCalendarContext();
  const { workspaceInfo } = useWorkspaceContext();

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [copyEvents] = useState(events);

  let views: tCalendarView[] = [];

  if (currentTab === eAgendaTabs.EVENTS) {
    views = [
      {
        name: 'Día',
        view: eCalendarView.LIST_DAY,
      },
      {
        name: 'Semana',
        view: eCalendarView.LIST_WEEK,
      },
      {
        name: 'Mes',
        view: eCalendarView.LIST_MONTH,
      },
      {
        name: 'Año',
        view: eCalendarView.LIST_YEAR,
      },
    ];
  }

  if (currentTab === eAgendaTabs.CALENDAR) {
    views = [
      {
        name: 'Mes',
        view: eCalendarView.DAY_GRID_MONTH,
      },
      {
        name: 'Semana',
        view: eCalendarView.TIME_GRID_WEEK,
      },
      {
        name: 'Día',
        view: eCalendarView.TIME_GRID_DAY,
      },
    ];
  }

  if (currentTab === eAgendaTabs.TEAM) {
    views = [
      {
        name: 'Día',
        view: eCalendarView.RESOURCE_TIME_GRID_DAY,
      },
    ];
  }

  function addUserIdToFilter(userId: string) {
    const nextEvents: EventInput[] = [];

    if (selectedUsers.includes(userId)) {
      selectedUsers.splice(selectedUsers.indexOf(userId), 1);
    } else {
      selectedUsers.push(userId);
    }

    setSelectedUsers(selectedUsers);

    if (selectedUsers.length) {
      for (const selectedUser of selectedUsers) {
        for (const event of copyEvents) {
          const guests = event?.data?.guests || [];

          if (guests.includes(selectedUser)) {
            const eventExists = nextEvents.find((e) => e.id === event.id);

            if (!eventExists) {
              nextEvents.push(event);
            }
          }
        }
      }

      setEvents(nextEvents);
    } else {
      setEvents(copyEvents);
    }
  }

  const avatars = Object.values(workspaceInfo?.users ?? {}).map((user) => ({
    letters: GetInitialsName(user.name),
    src: user.profilePicture,
    tooltipLabel: user.name,
    onClick: () => addUserIdToFilter(user.authId),
    isSelected: selectedUsers.includes(user.authId),
  }));

  return (
    <div className="HeaderToolbar">
      <div className="flex items-center gap-2">
        <HeaderDatePicker
          onChangeDatepicker={onChangeDatepicker}
          currentDate={currentDate}
        />
        <button onClick={() => onNavigate('prev')}>
          <ChevronLeftIcon className="h-8 w-8 text-primary" />
        </button>

        <h3 className="text-xl font-bold w-52 text-center">
          {currentDateTitle}
        </h3>

        <button onClick={() => onNavigate('next')}>
          <ChevronRightIcon className="h-8 w-8 text-primary" />
        </button>

        <div className="flex">
          <Avatars avatars={avatars} maxItems={15} showSelected />
        </div>
        {loading ? (
          <div className="flex items-center justify-center ">
            <Loading color="primary" size="md" />
          </div>
        ) : null}
      </div>

      <div className="flex items-center">
        {views.map((view, index) => {
          const viewsClassNames = classNames('px-8 py-2 text-center', {
            'rounded-l-md': index === 0,
            'rounded-r-md': index === views.length - 1,
            'bg-primary text-base-100': currentView === view.view,
            'bg-base-100': currentView !== view.view,
          });

          return (
            <button
              key={randomString()}
              className={viewsClassNames}
              onClick={() => changeView(view.view)}
            >
              {view.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
